// Client-side project renderer.
//
// Mirrors backend/projectExporter.js (exportFinal / exportStems) but runs
// entirely in the browser via OfflineAudioContext, so the heavy mixdown
// uses the user's CPU instead of the server. This is the SaaS export path:
// the server only ever ships the declarative project (the matcher/aligner
// output), never the rendering — and never freezes under concurrent
// exports because each export runs on a different user's machine.
//
// Render model (1:1 with audioEngine#play, minus the live transport):
//   • one GainNode per track (track.gain) → offline.destination
//   • one AudioBufferSourceNode per clip, started at clip.startTime with
//     source offset clip.sourceStart for clip.duration seconds
//   • gaps are free — nothing scheduled means silence
//   • the final summed buffer is encoded to a 16-bit PCM WAV
//
// exportFinal parity: only solo-aware audible tracks are summed.
// exportStems parity: every track rendered separately at full project
// length, gain applied, mute/solo NOT masked.

import {
  ensureProjectDecoded,
  getCachedAudioBuffer,
  sourceSampleRate,
} from './audioEngine.js';

const EPS = 1e-3;
const OUT_CHANNELS = 2;

// Solo-aware audible set — matches projectExporter.js#activeTracks.
function activeTracks(project) {
  const anySolo = project.tracks.some((t) => t.solo);
  return project.tracks.filter((t) => (anySolo ? t.solo : !t.muted));
}

// Sum the given tracks into one rendered AudioBuffer at full project length.
// Track gain is applied; mute/solo is the caller's concern (it picks which
// tracks to pass).
async function renderTracks(tracks, project, sampleRate) {
  const length = Math.max(1, Math.ceil(Math.max(EPS, Number(project.duration) || 0) * sampleRate));
  const offline = new OfflineAudioContext(OUT_CHANNELS, length, sampleRate);

  for (const track of tracks) {
    const ab = getCachedAudioBuffer(track.sourceFile);
    if (!ab) continue; // source missing/undecoded — skip silently, like the engine

    const gainNode = offline.createGain();
    const gain = Number(track.gain);
    gainNode.gain.value = Number.isFinite(gain) ? Math.max(0, gain) : 1;
    gainNode.connect(offline.destination);

    for (const clip of track.clips || []) {
      const duration = Number(clip.duration) || 0;
      if (duration <= EPS) continue;
      const node = offline.createBufferSource();
      node.buffer = ab;
      node.connect(gainNode);
      const when = Math.max(0, Number(clip.startTime) || 0);
      const offset = Math.max(0, Number(clip.sourceStart) || 0);
      try {
        node.start(when, offset, duration);
      } catch {
        // start() throws if when/offset fall outside the buffer — skip.
      }
    }
  }

  return offline.startRendering();
}

// Encode an AudioBuffer to a 16-bit PCM WAV Blob (matches the server's
// pcm_s16le output, at the source sample rate).
function audioBufferToWavBlob(buffer) {
  const numCh = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const blockAlign = numCh * 2; // 16-bit
  const dataSize = numFrames * blockAlign;
  const ab = new ArrayBuffer(44 + dataSize);
  const view = new DataView(ab);

  let p = 0;
  const str = (s) => { for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i)); };
  const u32 = (v) => { view.setUint32(p, v, true); p += 4; };
  const u16 = (v) => { view.setUint16(p, v, true); p += 2; };

  str('RIFF'); u32(36 + dataSize); str('WAVE');
  str('fmt '); u32(16); u16(1); u16(numCh);
  u32(sampleRate); u32(sampleRate * blockAlign); u16(blockAlign); u16(16);
  str('data'); u32(dataSize);

  const channels = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));
  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numCh; c++) {
      let s = channels[c][i];
      s = s < -1 ? -1 : s > 1 ? 1 : s;
      view.setInt16(p, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      p += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}

// Sanitize a track name into a unique .wav filename (mirrors exportStems).
function stemFilename(track, used) {
  let base = (track.name || track.id || 'track').replace(/[^\w.\- ]+/g, '_').slice(0, 60) || track.id;
  let name = `${base}.wav`;
  let n = 2;
  while (used.has(name.toLowerCase())) { name = `${base}_${n}.wav`; n += 1; }
  used.add(name.toLowerCase());
  return name;
}

// Render the final summed mix. Returns a WAV Blob. Throws if every track
// is muted (parity with the server).
export async function renderFinalMix(project) {
  await ensureProjectDecoded(project);
  const tracks = activeTracks(project);
  if (!tracks.length) throw new Error('Nothing to export — every track is muted.');
  const rendered = await renderTracks(tracks, project, sourceSampleRate());
  return audioBufferToWavBlob(rendered);
}

// Render one WAV per track at full project length. Returns
// [{ name, blob }]. Mute/solo do not mask stems; gain still applies.
export async function renderStems(project) {
  await ensureProjectDecoded(project);
  const sr = sourceSampleRate();
  const used = new Set();
  const out = [];
  for (const track of project.tracks) {
    const rendered = await renderTracks([track], project, sr);
    out.push({ name: stemFilename(track, used), blob: audioBufferToWavBlob(rendered) });
  }
  return out;
}
